import { apiDelete, apiGet, apiGetPaginated, apiPatch, apiPost, PaginatedResult } from '../client'
import { UserRole } from './auth'
import { MatchImmigrationContext, MatchTier } from './jobs'
import { ApplicationStatus } from './schoolAdmins'

export type AccountStatus = 'ACTIVE' | 'SUSPENDED' | 'DELETED' | 'PENDING_VERIFICATION'
export type VerificationStatus = 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'REJECTED'
export type JobStatus = 'DRAFT' | 'ACTIVE' | 'PAUSED' | 'CLOSED' | 'EXPIRED'
export type CaseStatus = 'OPEN' | 'IN_PROGRESS' | 'PENDING_DOCUMENTS' | 'SUBMITTED' | 'APPROVED' | 'DENIED' | 'CLOSED' | 'ON_HOLD'
export type EventStatus = 'DRAFT' | 'PUBLISHED' | 'CANCELLED' | 'COMPLETED' | 'INACTIVE'
export type InstitutionType = 'UNIVERSITY' | 'COLLEGE' | 'COMMUNITY_COLLEGE' | 'LAW_FIRM' | 'COMPANY' | 'STAFFING_AGENCY'
export type SubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'EXPIRED' | 'TRIALING' | 'PAUSED'
export type SubscriptionTier = 'FREE' | 'BASIC' | 'PRO' | 'STARTER' | 'GROWTH' | 'ENTERPRISE'
export type PaymentStatus = 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'REFUNDED' | 'PARTIALLY_REFUNDED'
export type AuditAction = 'CREATE' | 'UPDATE' | 'DELETE' | 'LOGIN' | 'LOGOUT' | 'VIEW' | 'EXPORT' | 'INVITE' | 'APPROVE' | 'REJECT'
export type UniversityRequestStatus = 'PENDING' | 'NOTIFIED' | 'REGISTERED' | 'CLOSED'
export type AttorneyAgreementStatus = 'PENDING' | 'SENT' | 'SIGNED' | 'UPDATE_REQUIRED'
export type FeedbackStatus = 'NEW' | 'REVIEWED' | 'PLANNED' | 'RESOLVED' | 'CLOSED'
export type FeedbackCategory = 'GENERAL' | 'BUG_REPORT' | 'FEATURE_REQUEST'
export interface PlatformStats {
  /** Institutions that are companies, staffing agencies or law firms */
  companyInstitutions?: number
  monthlyRevenue: number
  /** Institutions that are universities, colleges or community colleges */
  schoolInstitutions?: number
  totalActiveJobs: number
  totalApplications: number
  totalCases: number
  totalEmployers: number
  totalInstitutions: number
  totalJobs: number
  totalLawyers: number
  totalRevenue: number
  totalSchools: number
  totalStudents: number
  totalSubscriptions: number
  totalUsers: number
}

export interface AdminUserProfile {
  [key: string]: unknown
  avatarUrl?: string | null
  companyName?: string | null
  firstName: string
  id: string
  institution?: { name: string } | null
  lastName: string
  verificationStatus?: VerificationStatus
}

/** What an admin checks before verifying an attorney. */
export interface AdminLawyerProfile extends AdminUserProfile {
  agreementStatus?: AttorneyAgreementStatus
  availabilityNote?: string | null
  barAdmissions?: { licenseNumber: string, state: string }[]
  consultationModes?: ('IN_PERSON' | 'VIRTUAL')[]
  consultationServices?: string[]
  firmWebsite?: string | null
  focusAreas?: string[]
  jurisdictions?: string[]
  languages?: string[]
  lawFirmName?: string | null
  licenseNumber?: string | null
  phoneNumber?: string | null
  schedulingUrl?: string | null
  yearsOfExperience?: number | null
}

export interface AdminUserSummary {
  [key: string]: unknown
  accountStatus: AccountStatus
  authProvider?: string
  createdAt: string
  email: string | null
  employer?: AdminUserProfile | null
  id: string
  immigrationLawyer?: AdminLawyerProfile | null
  isEmailVerified?: boolean
  isPhoneVerified?: boolean
  phone: string | null
  role: UserRole
  schoolAdmin?: AdminUserProfile | null
  student?: AdminUserProfile | null
}

export interface AdminUserDetail extends AdminUserSummary {
  auditLogs?: { action: AuditAction; createdAt: string; description: string; id: string }[]
  notifications?: { body: string; createdAt: string; id: string; isRead: boolean; title: string }[]
  subscription?: { id: string; plan: { name: string; tier: SubscriptionTier }; status: SubscriptionStatus } | null
}

export interface AdminAuditLog {
  [key: string]: unknown
  action: AuditAction
  actor?: { email: string; id: string; role: UserRole } | null
  actorId: string | null
  createdAt: string
  description: string
  entity: string
  entityId: string
  id: string
  metadata?: Record<string, unknown>
  subject?: { email: string; id: string; role: UserRole } | null
}

export interface AdminJobSummary {
  [key: string]: unknown
  _count?: { applications: number; savedBy: number }
  createdAt: string
  employer?: { companyName: string; id: string; user?: { email: string } } | null
  id: string
  isRemote: boolean
  location: string
  salaryMax?: number | null
  salaryMin?: number | null
  status: JobStatus
  title: string
}

export interface AdminJobDetail extends AdminJobSummary {
  applications?: {
    id: string
    status: ApplicationStatus
    student: { firstName: string; id: string; lastName: string; user?: { email: string } }
    submittedAt: string
  }[]
  description?: string
  employmentType?: string
  institution?: { id: string; name: string } | null
  requirements?: string
  sponsorshipAvailable?: boolean
}

export interface AdminApplicationSummary {
  [key: string]: unknown
  id: string
  job: { employerId: string; id: string; title: string }
  status: ApplicationStatus
  student: { firstName: string; id: string; lastName: string; user?: { email: string } }
  submittedAt: string
}

export interface AdminApplicationInterview {
  candidateFeedback?: string | null
  id: string
  interviewerEmail?: string | null
  interviewerFeedback?: string | null
  interviewerName?: string | null
  meetingLink?: string | null
  scheduledAt: string
  status: string
  type: string
}

export interface AdminApplicationOffer {
  acceptedAt?: string | null
  declinedAt?: string | null
  expiresAt?: string | null
  id: string
  salary?: number | null
  salaryCurrency: string
  startDate?: string | null
  status: string
  title: string
}

export interface AdminApplicationDetail extends AdminApplicationSummary {
  additionalInfo?: string | null
  coverLetter?: string | null
  interviews?: AdminApplicationInterview[]
  job: AdminApplicationSummary['job'] & { employer?: { companyName: string; user?: { email: string } } }
  offer?: AdminApplicationOffer | null
  rejectionReason?: string | null
  resumeUrl?: string | null
  reviewedAt?: string | null
  withdrawnReason?: string | null
}

export interface AdminCaseSummary {
  [key: string]: unknown
  _count?: { documents: number; tasks: number }
  createdAt: string
  id: string
  lawyer?: { firstName: string; id: string; lastName: string; user?: { email: string } } | null
  priority: string
  status: CaseStatus
  title: string
  type: string
}

export interface AdminCaseDetail extends AdminCaseSummary {
  description?: string
  documents?: { id: string; name: string; url: string }[]
  tasks?: { id: string; status: string; title: string }[]
  timeline?: { createdAt: string; description: string; eventType: string; id: string }[]
  uscisNumber?: string
}

export interface AdminSubscriptionSummary {
  [key: string]: unknown
  _count?: { invoices: number; payments: number }
  billingInterval: string
  currentPeriodEnd: string | null
  id: string
  plan: { id: string; name: string; tier: SubscriptionTier }
  status: SubscriptionStatus
  user: { email: string | null; id: string; role: UserRole }
}

export interface AdminSubscriptionDetail extends AdminSubscriptionSummary {
  currentPeriodStart?: string | null
  invoices?: { amountDue: number; id: string; status: string }[]
  payments?: { amount: number; createdAt: string; id: string; status: PaymentStatus }[]
}

export interface AdminInstitution {
  [key: string]: unknown
  _count?: { employers: number; events: number; jobs: number; schoolAdmins: number; students: number }
  address?: string | null
  city?: string | null
  country?: string
  createdAt?: string
  description?: string | null
  domain: string | null
  id: string
  isActive: boolean
  isSponsoringStudents?: boolean
  logoUrl?: string | null
  name: string
  slug: string
  sponsoredTier?: SubscriptionTier | null
  state?: string | null
  type: InstitutionType
  website?: string | null
}

export interface AdminInstitutionSchoolAdmin {
  firstName: string
  id: string
  lastName: string
  title?: string | null
  user: { email: string }
  verificationStatus: string
}

export interface AdminInstitutionDetail extends AdminInstitution {
  bannerUrl?: string | null
  schoolAdmins: AdminInstitutionSchoolAdmin[]
}

export interface AdminUniversityRequest {
  [key: string]: unknown
  createdAt: string
  id: string
  matchedInstitutionId?: string | null
  schoolContactEmail?: string
  schoolName: string
  schoolNotifiedAt?: string | null
  status: UniversityRequestStatus
  studentEmail: string
  studentFullName: string
  studentNotifiedAt?: string | null
}

export interface AdminEvent {
  [key: string]: unknown
  _count?: { rsvps: number }
  id: string
  location?: string | null
  startAt: string
  status: EventStatus
  title: string
  type: string
}

export interface AdminEventRsvp {
  createdAt: string
  id: string
  status: string
  student: { firstName: string; id: string; lastName: string; user?: { email: string } }
}

export interface AdminEventDetail extends AdminEvent {
  description?: string | null
  employer?: { companyName: string; user?: { email: string } } | null
  endAt: string
  institution?: { id: string; name: string } | null
  isVirtual: boolean
  meetingLink?: string | null
  rsvps: AdminEventRsvp[]
  schoolAdmin?: { firstName: string; lastName: string; user?: { email: string } } | null
  timezone: string
}

export interface AdminConversationParticipant {
  isAdmin: boolean
  joinedAt: string
  lastReadAt: string | null
  user: { email: string | null; id: string; role: UserRole } | null
  userId: string
}

export interface AdminConversation {
  [key: string]: unknown
  _count?: { messages: number }
  createdAt: string
  id: string
  isGroup: boolean
  participants: AdminConversationParticipant[]
  title?: string | null
  updatedAt: string
}

export interface AdminConversationPersona {
  employer?: { firstName: string; lastName: string } | null
  id: string
  immigrationLawyer?: { firstName: string; lastName: string } | null
  role?: UserRole
  schoolAdmin?: { firstName: string; lastName: string } | null
  student?: { firstName: string; lastName: string } | null
}

export interface AdminConversationMessage {
  attachmentType?: string | null
  attachmentUrl?: string | null
  content: string
  createdAt: string
  editedAt?: string | null
  id: string
  isDeleted: boolean
  sender: AdminConversationPersona | null
  senderId: string
}

export interface AdminConversationDetail extends AdminConversation {
  messages: AdminConversationMessage[]
  participants: (AdminConversationParticipant & { user: AdminConversationPersona | null })[]
}

export interface AdminNotification {
  [key: string]: unknown
  body: string
  createdAt: string
  id: string
  isRead: boolean
  title: string
  type: string
  userId: string
}

export interface AdminWaitlistEntry {
  [key: string]: unknown
  createdAt: string
  email: string
  id: string
  name?: string | null
  role?: string | null
  source?: string | null
}

export interface AdminPayment {
  [key: string]: unknown
  amount: number
  createdAt: string
  currency: string
  id: string
  status: PaymentStatus
  subscription?: { plan?: { name: string; tier: SubscriptionTier }; user?: { email: string | null } } | null
}

export interface RevenueStats {
  planBreakdown: { count: number; plan: string; tier: SubscriptionTier }[]
  subscriptionsByStatus: { _count: { id: number }; status: SubscriptionStatus }[]
  total: number
  yearly: number
}

export interface AdminPlan {
  [key: string]: unknown
  _count?: { subscriptions: number }
  features: string[]
  id: string
  isActive: boolean
  name: string
  priceMonthly: number | null
  priceQuarterly: number | null
  priceYearly: number | null
  tier: SubscriptionTier
}

export interface AdminAttorneyAgreement {
  [key: string]: unknown
  id: string
  lawyer: { firstName: string; id: string; lastName: string; lawFirmName?: string | null; user: { email: string | null } }
  remindersSentDays: number[]
  sentAt?: string | null
  signedAt?: string | null
  status: AttorneyAgreementStatus
  version?: string | null
}

export interface AdminFeedback {
  [key: string]: unknown
  category: FeedbackCategory
  createdAt: string
  id: string
  lastLoginAtSnapshot: string | null
  planTierSnapshot: SubscriptionTier | null
  roleSnapshot: UserRole
  screenshotUrl: string | null
  source: string | null
  status: FeedbackStatus
  updatedAt: string
  user: { email: string | null; id: string; role?: UserRole; student?: { firstName: string; lastName: string } | null }
  userId: string
  whatWasConfusing: string | null
  whatWorkedWell: string | null
  whatWouldChange: string | null
}

export interface CreateInstitutionPayload {
  description?: string
  domain?: string
  isActive?: boolean
  isSponsoringStudents?: boolean
  name?: string
  slug?: string
  sponsoredTier?: SubscriptionTier
  type?: InstitutionType
  website?: string
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

export const getPlatformStats = () => apiGet<PlatformStats>('/super-admin/dashboard/stats')

// ─── Users ───────────────────────────────────────────────────────────────────

export const listUsers = (params?: { accountStatus?: AccountStatus; limit?: number; page?: number; role?: UserRole; search?: string }): Promise<PaginatedResult<AdminUserSummary[]>> =>
  apiGetPaginated<AdminUserSummary[]>('/super-admin/users', { params })

export const getUser = (userId: string) => apiGet<AdminUserDetail>(`/super-admin/users/${userId}`)

export const updateUserStatus = (userId: string, accountStatus: AccountStatus) => apiPatch<AdminUserSummary>(`/super-admin/users/${userId}/status`, { accountStatus })

/** White-glove account setup: assigns a user's login email without requiring
 * OTP verification of an old email they've never proven ownership of. The
 * new email is marked unverified until they log in and verify it themselves. */
export const adminSetUserEmail = (userId: string, newEmail: string, reason: string) =>
  apiPatch<{ email: string; id: string }>(`/super-admin/users/${userId}/email`, { newEmail, reason })

export interface ImpersonationResult {
  accessToken: string
  expiresInSeconds: number
  user: { id: string; name: string; role: UserRole }
}

export const impersonateUser = (userId: string) => apiPost<ImpersonationResult>(`/super-admin/users/${userId}/impersonate`)

export const verifyUser = (userId: string, verificationStatus: VerificationStatus, reason?: string) =>
  apiPatch<{ fullName: string; newStatus: VerificationStatus; previousStatus: VerificationStatus; profileId: string; role: UserRole; userId: string }>(`/super-admin/users/${userId}/verify`, { reason, verificationStatus })

export interface RecoveryTokenResult {
  expiresAt: string
  target: { email: string | null; id: string; role: UserRole }
  token: string
}

export const issueRecoveryToken = (userId: string, reason: string) =>
  apiPost<RecoveryTokenResult>(`/super-admin/users/${userId}/recovery-token`, { reason })

// ─── Audit logs ──────────────────────────────────────────────────────────────

export const listAuditLogs = (params?: { action?: AuditAction; actorEmail?: string; endDate?: string; entity?: string; limit?: number; page?: number; startDate?: string }): Promise<PaginatedResult<AdminAuditLog[]>> =>
  apiGetPaginated<AdminAuditLog[]>('/super-admin/audit-logs', { params })

// ─── Signup validation failures ────────────────────────────────────────────

export interface AdminSignupValidationFailure {
  [key: string]: unknown
  createdAt: string
  email: string | null
  endpoint: string
  errorMessages: string[]
  id: string
  statusCode: number
}

export const listSignupValidationFailures = (params?: { email?: string; endDate?: string; endpoint?: string; limit?: number; page?: number; startDate?: string }): Promise<PaginatedResult<AdminSignupValidationFailure[]>> =>
  apiGetPaginated<AdminSignupValidationFailure[]>('/super-admin/signup-validation-failures', { params })

export type SignupFunnelStep = 'STARTED' | 'OTP_SENT' | 'OTP_VERIFIED' | 'ACCOUNT_CREATED' | 'ONBOARDING_COMPLETE'

/** One row per email attempting to register, updated live as they progress
 * through the funnel — the counterpart to AdminSignupValidationFailure,
 * which only ever sees rejected requests. `error` is the most recent
 * validation failure recorded for that email, if any (may be non-null even
 * on a completed attempt — it just reflects something that happened along
 * the way, not necessarily what's currently blocking them). */
export interface AdminSignupAttempt {
  [key: string]: unknown
  accountCreatedAt: string | null
  accountType: UserRole
  email: string
  error: string | null
  id: string
  lastActivityAt: string
  lastStep: SignupFunnelStep
  onboardingCompletedAt: string | null
  otpSentAt: string | null
  otpSentCount: number
  otpVerifiedAt: string | null
  startedAt: string
  userId: string | null
}

/** The overview charts: the last 12 weeks or months, oldest first (the last one is still in progress). */
export type TrendPeriod = 'months' | 'weeks'

export interface DashboardTrendBucket {
  applications: number
  jobsPosted: number
  /** cents */
  revenue: number
  signups: { attorneys: number, employers: number, schoolAdmins: number, students: number }
  start: string
}

export interface DashboardTrends {
  /** applications sent in the period, by where they stand now */
  applicationStatus: Partial<Record<ApplicationStatus, number>>
  buckets: DashboardTrendBucket[]
  period: TrendPeriod
  /** sign-ups started in the period, by how far they got */
  signupFunnel: { accountCreated: number, codeSent: number, finished: number, started: number, verified: number }
}

export const getDashboardTrends = (period: TrendPeriod) => apiGet<DashboardTrends>('/super-admin/dashboard/trends', { params: { period } })

export const listSignupAttempts = (params?: { accountType?: UserRole; email?: string; endDate?: string; lastStep?: SignupFunnelStep; limit?: number; page?: number; startDate?: string }): Promise<PaginatedResult<AdminSignupAttempt[]>> =>
  apiGetPaginated<AdminSignupAttempt[]>('/super-admin/signup-attempts', { params })

/** Delivered/bounced/opened/clicked events from ZeptoMail. Empty until the
 * webhook is registered in the ZeptoMail dashboard (Agents → your agent →
 * Webhooks → Add Webhook, URL: <api-base>/webhooks/zeptomail). */
export interface AdminEmailDeliveryEvent {
  [key: string]: unknown
  eventType: string
  id: string
  reason: string | null
  receivedAt: string
  recipient: string | null
  signatureVerified: boolean
  subject: string | null
}

export const listEmailDeliveryEvents = (params?: { endDate?: string; eventType?: string; limit?: number; page?: number; recipient?: string; startDate?: string }): Promise<PaginatedResult<AdminEmailDeliveryEvent[]>> =>
  apiGetPaginated<AdminEmailDeliveryEvent[]>('/super-admin/email-delivery-events', { params })

// ─── Job match review ────────────────────────────────────────────────────────

export type AdminMatchTier = MatchTier | 'BELOW' | 'NEAR_MISS'

export type AdminMatchView = 'ALL' | 'POSSIBLY_MISSED' | 'SAMPLE' | Exclude<AdminMatchTier, 'BELOW'>

export interface AdminMatchFlag {
  code: 'EXPERIENCE_GAP' | 'FEW_SKILLS_MATCHED' | 'INCOMPLETE_PROFILE' | 'JOB_HAS_NO_SKILLS' | 'POSSIBLY_MISSED' | 'STUDENT_HAS_NO_SKILLS'
  label: string
}

export interface AdminMatch {
  applicationStatus: string | null
  breakdown: { careerInterest: number; education: number; employmentType: number; experience: number; location: number; skills: number }
  considerations: string[]
  experience: { jobLevel: string | null; studentLevel: string }
  flags: AdminMatchFlag[]
  id: string
  immigration: MatchImmigrationContext
  job: { company: string | null; employmentType: string; id: string; isRemote: boolean; location: string | null; title: string }
  reasons: string[]
  score: number
  skills: { matched: string[]; missing: string[]; related: string[]; usedTextFallback: boolean }
  student: { email: string | null; id: string; major: string | null; name: string; profileCompletion: number }
  tier: AdminMatchTier
}

export interface AdminMatchSummary {
  appliedMatches: number
  byTier: Record<MatchTier, number>
  computedAt: string
  flaggedMatches: number
  jobsWithMatches: number
  nearMisses: number
  pendingMatches: number
  possiblyMissed: number
  studentsWithMatches: number
  totalJobs: number
  totalMatches: number
  totalStudents: number
}

export interface AdminMatchQuery {
  applied?: 'applied' | 'not_applied'
  flaggedOnly?: 'true'
  jobId?: string
  limit?: number
  page?: number
  refresh?: 'true'
  search?: string
  studentId?: string
  tier?: AdminMatchView
}

export const listMatches = async (params: AdminMatchQuery): Promise<{ data: AdminMatch[]; pages: number; summary: AdminMatchSummary; total: number }> => {
  const result = await apiGetPaginated<AdminMatch[]>('/super-admin/matches', { params })
  const metadata = result.metadata as (typeof result.metadata & { summary: AdminMatchSummary }) | undefined
  return {
    data: result.data,
    pages: Math.max(1, metadata?.pages ?? 1),
    summary: metadata!.summary,
    total: metadata?.total ?? 0,
  }
}

// ─── Match-First employer pilot ──────────────────────────────────────────────

export type PilotJobStage = 'CLAIMED' | 'DRAFT' | 'INACTIVE' | 'PUBLISHED' | 'VERIFIED'
export type PilotJobAction = 'deactivate' | 'draft' | 'publish' | 'verify'

export interface PilotImportRow {
  action: 'create' | 'skip' | 'update'
  employer: string | null
  errors: string[]
  pilotId: string | null
  rowNumber: number
  title: string | null
  warnings: string[]
}

export interface PilotImportResult {
  committed: boolean
  rows: PilotImportRow[]
  sheet: string
  summary: { create: number; employers: number; rows: number; skipped: number; update: number; withWarnings: number }
}

export interface PilotJob {
  experienceLevel: string | null
  externalRef: string | null
  id: string
  location: string | null
  matches: number | null
  prospect: { contactEmail: string | null; contactName: string | null; id: string; name: string; outreachStatus: string } | null
  skillsPreferred: string[]
  skillsRequired: string[]
  sourceUrl: string | null
  stage: PilotJobStage
  title: string
  topScore: number | null
}

export interface PilotJobsResult {
  jobs: PilotJob[]
  summary: { claimed: number; draft: number; employers: number; inactive: number; jobsWithMatches: number; published: number; total: number; verified: number }
}

export const importPilotJobs = (file: File, commit: boolean) => {
  const form = new FormData()
  form.append('file', file)
  return apiPost<PilotImportResult>(`/super-admin/pilot/import${commit ? '?commit=true' : ''}`, form)
}

export type ProspectOutreachStatus = 'CLAIMED' | 'CONTACTED' | 'DECLINED' | 'MEETING_BOOKED' | 'NOT_CONTACTED' | 'REGISTERED' | 'RESPONDED'

export interface PilotCandidate {
  blockedReason: string | null
  field: string | null
  flags: string[]
  name: string
  profileCompletion: number
  score: number
  skillsListed: number
  skillsMatched: number
  studentId: string
  tier: AdminMatchTier
  workAuthorized: boolean
}

export interface PilotProspect {
  claimedAt: string | null
  claimedBy: { company: string | null; email: string | null } | null
  contactEmail: string | null
  contactName: string | null
  contactTitle: string | null
  contactedAt: string | null
  domain: string | null
  id: string
  jobs: { id: string; matches: number | null; stage: PilotJobStage; title: string }[]
  meetingAt: string | null
  name: string
  notes: string | null
  outreachStatus: ProspectOutreachStatus
  pendingClaims: { createdAt: string; email: string; id: string }[]
  previews: { candidateCount: number; createdAt: string; id: string; jobTitle: string | null; lastViewedAt: string | null; revoked: boolean; url: string; viewCount: number }[]
  registeredAt: string | null
  respondedAt: string | null
  website: string | null
}

export interface PilotReport {
  employers: { claimed: number; contacted: number; declined: number; meetings: number; paid: number; registered: number; responded: number; responseRate: number | null; total: number }
  jobs: { published: number; total: number; verified: number; withMatches: number }
  previews: { created: number; viewed: number; views: number }
  revenueCents: number
}

export const getPilotCandidates = (jobId: string) =>
  apiGet<{ candidates: PilotCandidate[]; job: { id: string; published: boolean; title: string }; noSponsorship: boolean }>(`/super-admin/pilot/jobs/${jobId}/candidates`)

export const createPilotPreview = (jobId: string, studentIds: string[]) =>
  apiPost<{ id: string; url: string }>('/super-admin/pilot/previews', { jobId, studentIds })

export const revokePilotPreview = (id: string) => apiDelete<null>(`/super-admin/pilot/previews/${id}`)

export const getPilotProspects = () => apiGet<PilotProspect[]>('/super-admin/pilot/prospects')

export const updatePilotProspect = (id: string, patch: { notes?: string; outreachStatus?: Exclude<ProspectOutreachStatus, 'CLAIMED'> }) =>
  apiPatch<null>(`/super-admin/pilot/prospects/${id}`, patch)

export const decidePilotClaim = (id: string, approve: boolean) => apiPost<null>(`/super-admin/pilot/claims/${id}/${approve ? 'approve' : 'reject'}`)

export const getPilotReport = () => apiGet<PilotReport>('/super-admin/pilot/report')

export const getPilotJobs = () => apiGet<PilotJobsResult>('/super-admin/pilot/jobs')

export const changePilotJobStatus = (ids: string[], action: PilotJobAction) =>
  apiPost<{ skipped: { id: string; reason: string }[]; updated: number }>('/super-admin/pilot/jobs/status', { action, ids })

// ─── Notification settings (automated email switches) ────────────────────────

export interface AdminNotificationCategory {
  audience: string
  defaultEnabled: boolean
  description: string
  enabled: boolean
  essential: boolean
  key: string
  label: string
  phase: number
}

export interface AdminNotificationSettings {
  categories: AdminNotificationCategory[]
  // Keyed by EmailSendLog category (e.g. WEEKLY_SUMMARY_STUDENT, INTERVIEW).
  sentLast7Days: Record<string, number>
}

export const getNotificationSettings = () => apiGet<AdminNotificationSettings>('/super-admin/notification-settings')

export const updateNotificationSetting = (key: string, enabled: boolean) =>
  apiPatch<AdminNotificationSettings>(`/super-admin/notification-settings/${key}`, { enabled })

// ─── Summary count cards ─────────────────────────────────────────────────────

export type SummaryTone = 'bad' | 'good' | 'neutral' | 'warn'

export interface SummaryCard {
  label: string
  tone: SummaryTone
  value: number
}

export const getModuleSummary = (module: string) => apiGet<SummaryCard[]>(`/super-admin/summary/${module}`)

// ─── Payment issues audit ────────────────────────────────────────────────────

export type PaymentIssueType = 'INVOICE_PAYMENT_FAILED' | 'PAYMENT_INTENT_FAILED' | 'WEBHOOK_PROCESSING_ERROR'

export interface AdminPaymentIssueUser {
  accountStatus: string
  email: string | null
  employer?: { firstName: string; lastName: string } | null
  id: string
  immigrationLawyer?: { firstName: string; lastName: string } | null
  phone: string | null
  role: UserRole
  schoolAdmin?: { firstName: string; lastName: string } | null
  student?: { firstName: string; lastName: string } | null
}

export interface AdminPaymentIssue {
  [key: string]: unknown
  amount: number | null
  attemptCount: number | null
  createdAt: string
  currency: string | null
  declineCode: string | null
  errorCode: string | null
  errorMessage: string | null
  id: string
  invoiceNumber: string | null
  nextRetryAt: string | null
  paymentMethodType: string | null
  rawEvent: Record<string, unknown>
  resolutionNotes: string | null
  resolved: boolean
  resolvedAt: string | null
  resolvedBy: string | null
  stripeCustomerId: string | null
  stripeInvoiceId: string | null
  stripePaymentIntentId: string | null
  stripeSubscriptionId: string | null
  type: PaymentIssueType
  user: AdminPaymentIssueUser | null
  userId: string | null
}

export const listPaymentIssues = (params?: { endDate?: string; limit?: number; page?: number; resolved?: boolean; startDate?: string; type?: PaymentIssueType; userId?: string }): Promise<PaginatedResult<AdminPaymentIssue[]>> =>
  apiGetPaginated<AdminPaymentIssue[]>('/super-admin/payment-issues', { params })

export const getPaymentIssue = (id: string) => apiGet<AdminPaymentIssue>(`/super-admin/payment-issues/${id}`)

export const resolvePaymentIssue = (id: string, notes?: string) =>
  apiPatch<AdminPaymentIssue>(`/super-admin/payment-issues/${id}/resolve`, { notes })

// ─── Attorney Network Participation Agreement ────────────────────────────────

export const listAttorneyAgreements = (params?: { limit?: number; page?: number; status?: AttorneyAgreementStatus }): Promise<PaginatedResult<AdminAttorneyAgreement[]>> =>
  apiGetPaginated<AdminAttorneyAgreement[]>('/super-admin/attorney-agreements', { params })

export const resendAttorneyAgreement = (lawyerId: string) =>
  apiPatch<null>(`/super-admin/attorney-agreements/${lawyerId}/resend`)

// ─── User Feedback ───────────────────────────────────────────────────────────

export const listFeedback = (params?: { dateFrom?: string; dateTo?: string; limit?: number; page?: number; role?: UserRole; source?: string; status?: FeedbackStatus }): Promise<PaginatedResult<AdminFeedback[]>> =>
  apiGetPaginated<AdminFeedback[]>('/super-admin/feedback', { params })

export const getFeedback = (id: string) => apiGet<AdminFeedback>(`/super-admin/feedback/${id}`)

export const updateFeedbackStatus = (id: string, status: FeedbackStatus) =>
  apiPatch<AdminFeedback>(`/super-admin/feedback/${id}/status`, { status })

export const launchFeedbackCampaign = (campaignKey: string) =>
  apiPost<{ enrolled: number; totalEligible: number }>(`/super-admin/campaigns/${campaignKey}/launch`)

// ─── Jobs ────────────────────────────────────────────────────────────────────

export const listJobs = (params?: { employerId?: string; limit?: number; page?: number; search?: string; status?: JobStatus }): Promise<PaginatedResult<AdminJobSummary[]>> =>
  apiGetPaginated<AdminJobSummary[]>('/super-admin/jobs', { params })

export const getJob = (jobId: string) => apiGet<AdminJobDetail>(`/super-admin/jobs/${jobId}`)

export const updateJobStatus = (jobId: string, status: JobStatus) => apiPatch<{ id: string; status: JobStatus }>(`/super-admin/jobs/${jobId}/status`, { status })

// ─── Applications ────────────────────────────────────────────────────────────

export const listApplications = (params?: { jobId?: string; limit?: number; page?: number; studentId?: string }): Promise<PaginatedResult<AdminApplicationSummary[]>> =>
  apiGetPaginated<AdminApplicationSummary[]>('/super-admin/applications', { params })

export const getApplication = (applicationId: string) => apiGet<AdminApplicationDetail>(`/super-admin/applications/${applicationId}`)

export const updateApplicationStatus = (applicationId: string, status: ApplicationStatus) =>
  apiPatch<{ id: string; reviewedAt: string; status: ApplicationStatus }>(`/super-admin/applications/${applicationId}/status`, { status })

// ─── Cases ───────────────────────────────────────────────────────────────────

export const listCases = (params?: { limit?: number; page?: number; search?: string; status?: CaseStatus }): Promise<PaginatedResult<AdminCaseSummary[]>> =>
  apiGetPaginated<AdminCaseSummary[]>('/super-admin/cases', { params })

export const getCase = (caseId: string) => apiGet<AdminCaseDetail>(`/super-admin/cases/${caseId}`)

// ─── Subscriptions & payments ────────────────────────────────────────────────

export const listSubscriptions = (params?: { limit?: number; page?: number; status?: SubscriptionStatus; userId?: string }): Promise<PaginatedResult<AdminSubscriptionSummary[]>> =>
  apiGetPaginated<AdminSubscriptionSummary[]>('/super-admin/subscriptions', { params })

export const getSubscription = (subscriptionId: string) => apiGet<AdminSubscriptionDetail>(`/super-admin/subscriptions/${subscriptionId}`)

export const overrideSubscriptionStatus = (subscriptionId: string, status: SubscriptionStatus, reason?: string) =>
  apiPatch<{ newStatus: string; previousStatus: string; subscriptionId: string }>(`/super-admin/subscriptions/${subscriptionId}/override-status`, { reason, status })

export const changeSubscriptionPlan = (subscriptionId: string, planId: string, reason?: string) =>
  apiPatch<{ newTier: SubscriptionTier; previousTier: SubscriptionTier; subscriptionId: string }>(`/super-admin/subscriptions/${subscriptionId}/change-plan`, { planId, reason })

export const extendSubscription = (subscriptionId: string, days: number, reason?: string) =>
  apiPost<{ days: number; newEndDate: string; subscriptionId: string }>(`/super-admin/subscriptions/${subscriptionId}/extend`, { days, reason })

export const listPayments = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<AdminPayment[]>> =>
  apiGetPaginated<AdminPayment[]>('/super-admin/payments', { params })

export const getRevenueStats = () => apiGet<RevenueStats>('/super-admin/revenue')

export interface PaymentsBackfillResult {
  created: { amount: number; currency: string; invoiceNumber: string; subscriptionId: string; userId: string }[]
  dryRun: boolean
  invoicesChecked: number
  invoicesCreated: number
  paymentsCreated: number
  skippedExisting: number
  subscriptionsChecked: number
}

export const backfillPayments = (dryRun: boolean) => apiPost<PaymentsBackfillResult>(`/super-admin/payments/backfill?dryRun=${dryRun}`)

export const listPlans = () => apiGet<AdminPlan[]>('/super-admin/plans')

// ─── Institutions ────────────────────────────────────────────────────────────

export type InstitutionGroup = 'companies' | 'schools'

export const listInstitutions = (params?: { group?: InstitutionGroup; limit?: number; page?: number; search?: string; type?: InstitutionType }): Promise<PaginatedResult<AdminInstitution[]>> =>
  apiGetPaginated<AdminInstitution[]>('/super-admin/institutions', { params })

export const getInstitution = (institutionId: string) => apiGet<AdminInstitutionDetail>(`/super-admin/institutions/${institutionId}`)

export const createInstitution = (payload: CreateInstitutionPayload) => apiPost<AdminInstitution>('/super-admin/institutions', payload)

export const updateInstitution = (institutionId: string, payload: CreateInstitutionPayload) => apiPatch<AdminInstitution>(`/super-admin/institutions/${institutionId}`, payload)

export const deleteInstitution = (institutionId: string) => apiDelete<{ id: string }>(`/super-admin/institutions/${institutionId}`)

// ─── University requests ─────────────────────────────────────────────────────

export const listUniversityRequests = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<AdminUniversityRequest[]>> =>
  apiGetPaginated<AdminUniversityRequest[]>('/super-admin/university-requests', { params })

export const resolveUniversityRequest = (requestId: string, institutionId: string) =>
  apiPatch<AdminUniversityRequest>(`/super-admin/university-requests/${requestId}/resolve`, { institutionId })

// ─── Events ──────────────────────────────────────────────────────────────────

export const listEvents = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<AdminEvent[]>> =>
  apiGetPaginated<AdminEvent[]>('/super-admin/events', { params })

export const getEvent = (eventId: string) => apiGet<AdminEventDetail>(`/super-admin/events/${eventId}`)

// ─── Conversations & notifications ───────────────────────────────────────────

export const listConversations = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<AdminConversation[]>> =>
  apiGetPaginated<AdminConversation[]>('/super-admin/conversations', { params })

export const getConversation = (conversationId: string) => apiGet<AdminConversationDetail>(`/super-admin/conversations/${conversationId}`)

export const listNotifications = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<AdminNotification[]>> =>
  apiGetPaginated<AdminNotification[]>('/super-admin/notifications', { params })

// ─── Waitlist ────────────────────────────────────────────────────────────────

export const listWaitlist = (params?: { limit?: number; page?: number }): Promise<PaginatedResult<AdminWaitlistEntry[]>> =>
  apiGetPaginated<AdminWaitlistEntry[]>('/super-admin/waitlist', { params })

// ─── System ──────────────────────────────────────────────────────────────────

export const clearCache = () => apiPost<null>('/super-admin/cache/clear')

// ─── Website conversions ──────────────────────────────────────────────────────

export type ConversionRange = 7 | 30 | 90

export interface ConversionStep { count: number, key: string, label: string, ofPrevious: null | number }
export interface ConversionRole { completed: number, completionRate: null | number, role: UserRole, started: number, verified: number }

/** Website numbers from PostHog; each part is null if its query failed. */
export interface ConversionWeb {
  abTest: { clickRate: null | number, demoClicks: number, variant: string, visitors: number }[] | null
  ctaByAudience: { audience: string, clicks: number, people: number }[] | null
  demo: { clicks: number, people: number } | null
  funnel: { label: string, ofPrevious: null | number, people: number }[] | null
  pageVisitors: { label: string, path: string, visitors: number }[] | null
  sources: { conversionRate: null | number, signUpsCompleted: number, signUpsStarted: number, source: string, visitors: number }[] | null
  status: 'error' | 'ok'
  totals: { pageviews: number, visitors: number } | null
}

export interface ConversionDashboard {
  days: ConversionRange
  from: string
  signups: { byRole: ConversionRole[], completionRate: null | number, funnel: ConversionStep[], weekly: { completed: number, started: number, week: string }[] }
  to: string
  web: ConversionWeb | { status: 'not_configured' }
}

export const getConversions = (days: ConversionRange) => apiGet<ConversionDashboard>('/super-admin/conversions', { params: { days } })
